import {Component, OnInit, EventEmitter, Input} from '@angular/core';
import { CoordinateDiagnosis } from '../../../shared/model/coordinate-diagnosis';
import { DiagnosisWebSocketService } from '../../service/diagnosis-web-socket.service';
import { AttributeState } from '../../model/attribute-state';
import { DiagnosisService } from '../../service/diagnosis.service';
import { MaterializeAction } from 'angular2-materialize';
import { AccessoryRange } from '../../../shared/model/accessory-range';
import { ToastService } from '../../../shared/service/toast.service';

declare var Materialize: any;

@Component({
  selector: 'tx-coordinate-diagnosis',
  templateUrl: './coordinate-diagnosis.component.html',
  styleUrls: ['./coordinate-diagnosis.component.css']
})
export class CoordinateDiagnosisComponent implements OnInit {
  _coordinateDiagnosis: CoordinateDiagnosis;
  coordinate: any = {};
  loadingText: string;
  running = true;
  modalActions = new EventEmitter<string | MaterializeAction>();
  accessoryRange: AccessoryRange;
  range: any;
  _run: boolean;

  constructor(private _diagnosisNotification: DiagnosisWebSocketService,
              private _diagnosiService: DiagnosisService,
              private _toastService: ToastService) {
  }

  ngOnInit() {
    if (this._run) {
      this.observeRestart();
      this.setEmptyEntities();
      this.subscribeCoordinateSuccess();
      this.subscribeCoordinateFail();
    } else {
      this.displayViewMode();
    }
  }

  get run() {
    return this._run;
  }

  @Input()
  set run(run) {
    this._run = run;
    if (!this._run) {
      this.loadingText = 'Indisponível';
    }
  }

  get coordinateDiagnosis() {
    return this._coordinateDiagnosis;
  }

  @Input()
  set coordinateDiagnosis(coordinateDiagnosis) {
    this._coordinateDiagnosis = coordinateDiagnosis;
  }

  observeRestart() {
    this._diagnosisNotification.restart$
      .subscribe(() => {
        this.setEmptyEntities();
        this.running = true;
      });
  }

  setEmptyEntities() {
    this.loadingText = 'Carregando...';
    this.accessoryRange = new AccessoryRange();
    this.accessoryRange.ranges = new Array();
    this.coordinateDiagnosis = new CoordinateDiagnosis();
    this.coordinate = {};
    this.range = {};
    this.coordinate.canParams = {};
    this.coordinate.accessories = {};
  }

  subscribeCoordinateSuccess() {
    this._diagnosisNotification.success$
      .filter(data => data instanceof CoordinateDiagnosis)
      .subscribe(coordinateDiagnosis => {
        this.setAttributeState(coordinateDiagnosis, AttributeState.SUCCESS);
        this.updateDiagnosisDisplay(coordinateDiagnosis);
        this.loadingText = 'Indisponível';
      });
  }

  subscribeCoordinateFail() {
    this._diagnosisNotification.failed$
      .filter(data => data instanceof CoordinateDiagnosis)
      .subscribe(coordinateDiagnosis => {
        this.setAttributeState(coordinateDiagnosis, AttributeState.FAIL);
        this.updateDiagnosisDisplay(coordinateDiagnosis);
        this.loadingText = 'Indisponível';
      });
  }

  disablePanic() {
    this._diagnosiService.disablePanic()
      .subscribe(
        success => this.showMessage('Comando enviado.'),
        error => this.showMessage('Error ao enviar comando.')
      );
  }

  blockVehicle() {
    this._diagnosiService.blockVehicle()
      .subscribe(
        success => this.showMessage('Veículo bloqueado.'),
        error => this.showMessage('Erro ao enviar comando de bloqueio.')
      );
  }

  openModal() {
    this.modalActions.emit({action: 'modal', params: ['open']});
  }

  createAdmeasurement() {
    this.accessoryRange.ranges.push(this.range);
    this.accessoryRange.accessory = this.coordinateDiagnosis.accessories.RPM;
    this._diagnosiService.createAdmeasurement(this.accessoryRange)
      .subscribe(
        success => this.showMessage('Aferição de RPM criada!'),
        error => this.showMessage(JSON.parse(error._body).message)
      );
    this.modalActions.emit({action: 'modal', params: ['close']});
  }

  closeModal() {
    this.modalActions.emit({action: 'modal', params: ['close']});
  }

  getKeys(object): any {
    return Object.keys(object);
  }

  isSuccess(value) {
    return value === AttributeState.SUCCESS;
  }

  private updateDiagnosisDisplay(coordinateDiagnosis) {
    this.coordinateDiagnosis = coordinateDiagnosis;
    this.running = this.coordinateDiagnosis.isRunning;
  }

  private setAttributeState(coordinateResponse, state) {
    const accessoriesKey = 'accessories';
    const canParamsKey = 'canParams';
    Object.keys(coordinateResponse).forEach((key) => {
      if (!(key in this.coordinateDiagnosis) && key !== accessoriesKey && key !== canParamsKey) {
        this.coordinate[key] = state;
      }
    });
    Object.keys(coordinateResponse.accessories).forEach(key => {
      if (!(key in this.coordinateDiagnosis.accessories)) {
        this.coordinate.accessories[key] = state;
      }
    });
    Object.keys(coordinateResponse.canParams).forEach(key => {
      if (!(key in this.coordinateDiagnosis.canParams)) {
        this.coordinate.canParams[key] = state;
      }
    });
  }

  private showMessage(message) {
    this._toastService.showMessage(message, 4000);
  }

  private displayViewMode() {
    this.range = {};
    this.coordinate = this._coordinateDiagnosis;
    this.setAccessories();
    this.setCanParams();
    let coord = this._coordinateDiagnosis;
    this._coordinateDiagnosis = this._coordinateDiagnosis.coordinateData;
    this.setStateOnAttribute(coord);
  }

  private setStateOnAttribute(source) {
    Object.keys(source).forEach(key => {
      if (key !== 'accessories' && key !== 'canParams' && key !== 'peripheralEvents') {
        if (source[key]) {
          this.coordinate[key] = AttributeState.SUCCESS;
        } else {
          this.coordinate[key] = AttributeState.FAIL;
        }
      }
    });
  }

  private setAccessories() {
    this.coordinate.accessories = {'RPM': this._coordinateDiagnosis.rpmAccessory ? AttributeState.SUCCESS : AttributeState.FAIL,
      'PANIC': this._coordinateDiagnosis.panicAccessory ? AttributeState.SUCCESS : AttributeState.FAIL};
  }

  private setCanParams() {
    this.coordinate.canParams = {'RPM': this._coordinateDiagnosis.rpm ? AttributeState.SUCCESS : AttributeState.FAIL,
    'Fuel Flow': this._coordinateDiagnosis.fuelFlow ? AttributeState.SUCCESS : AttributeState.FAIL,
    'Brake Status': this._coordinateDiagnosis.brakeStatus ? AttributeState.SUCCESS : AttributeState.FAIL,
    'Engine Brake': this._coordinateDiagnosis.engineBrake ? AttributeState.SUCCESS : AttributeState.FAIL,
    'Engine Failure': this._coordinateDiagnosis.engineFailure ? AttributeState.SUCCESS : AttributeState.FAIL,
    'Engine Temperature': this._coordinateDiagnosis.engineTemperature ? AttributeState.SUCCESS : AttributeState.FAIL,
    'Fuel Level': this._coordinateDiagnosis.fuelLevel ? AttributeState.SUCCESS : AttributeState.FAIL,
    'Hodometer': this._coordinateDiagnosis.hodometer ? AttributeState.SUCCESS : AttributeState.FAIL,
    'Secondary Hodometer': this._coordinateDiagnosis.secondaryHodometer ? AttributeState.SUCCESS : AttributeState.FAIL,
    'Total Engine Hour': this._coordinateDiagnosis.totalEngineHour ? AttributeState.SUCCESS : AttributeState.FAIL};
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/production-argos-front/argos/src/app/diagnosis/component/coordinate-diagnosis/coordinate-diagnosis.component.ts